import { IconChevronLeft, IconChevronRight, IconHistory } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Banner, Dropdown } from '@/components/common';
import { isTodaysVisit, type VisitStep } from '@/utils/visitHistory';
import { formatDate } from './format';

type StepperProps = {
  steps: VisitStep[];
  /** null = the current state. */
  index: number | null;
  onChange: (index: number | null) => void;
};

/** ◀ visit ▶ with a dropdown to jump to any visit; the last option is "now". */
export function VisitStepper({ steps, index, onChange }: StepperProps) {
  const { t } = useTranslation();
  if (!steps.length) return null;
  const last = steps.length - 1;
  // From "now", the first step back is the last visit that differs from now.
  const lastPast = isTodaysVisit(steps[last]) ? last - 1 : last;
  const prev = () => onChange(index === null ? lastPast : Math.max(0, index - 1));
  const next = () => onChange(index === null || index >= last ? null : index + 1);

  return (
    <div className="visit-stepper">
      <button
        type="button"
        className="icon-btn"
        onClick={prev}
        disabled={index === 0 || (index === null && lastPast < 0)}
        aria-label={t('chart.visits.prev')}
      >
        <IconChevronLeft size={22} />
      </button>
      <Dropdown
        variant="toolbar"
        ariaLabel={t('chart.visits.select')}
        value={index === null ? 'now' : String(index)}
        options={[
          ...steps.map((s, i) => ({
            value: String(i),
            label: [
              t('chart.visits.option', {
                index: i + 1,
                total: steps.length,
                date: formatDate(s.visit.startedAt),
              }),
              // A visit saved without a doctor names none, rather than a bare dash.
              s.visit.dentist?.name,
              s.visit.fiscal ? t('chart.visit.fiscal') : null,
            ]
              .filter(Boolean)
              .join(' · '),
          })),
          { value: 'now', label: t('chart.visits.now') },
        ]}
        onChange={(v) => onChange(v === 'now' ? null : Number(v))}
      />
      <button
        type="button"
        className="icon-btn"
        onClick={next}
        disabled={index === null}
        aria-label={t('chart.visits.next')}
      >
        <IconChevronRight size={22} />
      </button>
    </div>
  );
}

/** Above the chart while an earlier visit is shown: its doctor (else its date), and the way back. */
export function HistoryBanner({ step, onBack }: { step: VisitStep; onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <Banner
      tone="warning"
      action={
        <button type="button" className="btn btn--sm btn--dark" onClick={onBack}>
          {t('chart.visits.backToNow')}
        </button>
      }
    >
      <IconHistory size={16} aria-hidden="true" style={{ verticalAlign: '-3px', marginRight: 4 }} />
      <strong>{step.visit.dentist?.name ?? formatDate(step.visit.startedAt)}</strong>
    </Banner>
  );
}
